import { useState } from 'react';
import { useApi } from '../lib/use-api';
import { money, shortDate } from '../lib/api';
import { Empty, ErrorBanner, Loading, Status } from '../components/ui';

type Tab = 'billing' | 'payouts' | 'tax';

interface Invoice {
  id: string;
  invoice_number: string;
  business_group_name: string | null;
  period_start: string;
  period_end: string;
  total: string;
  currency: string;
  status: string;
}

interface Payout {
  id: string;
  business_group_name: string;
  period_start: string;
  gross_collected: string;
  revenue_share_pct: string;
  payout_amount: string;
  currency: string;
  status: string;
}

interface TaxRow {
  business_group: string | null;
  tax_registration_number: string | null;
  currency: string;
  transactions: number;
  gross_amount: string;
  tax_collected: string;
}

/** Wireframe C3 — billing, payouts and tax reporting. */
export function Financials() {
  const [tab, setTab] = useState<Tab>('billing');
  const today = new Date();
  const [from, setFrom] = useState(new Date(today.getFullYear(), today.getMonth() - 1, 1).toISOString().slice(0, 10));
  const [to, setTo] = useState(today.toISOString().slice(0, 10));

  const invoices = useApi<{ items: Invoice[] }>(tab === 'billing' ? '/internal/financials/invoices' : null);
  const payouts = useApi<{ items: Payout[] }>(tab === 'payouts' ? '/internal/financials/payouts' : null);
  const tax = useApi<{ items: TaxRow[] }>(
    tab === 'tax' ? `/internal/financials/tax-report?from=${from}&to=${to}` : null,
    [from, to],
  );

  /**
   * Exports the visible table as CSV (NFR-C4).
   *
   * Generated client-side from data already on screen, so what finance opens
   * in a spreadsheet is exactly what they were looking at — no second query
   * that might disagree with the first.
   */
  function exportCsv(): void {
    const rows =
      tab === 'billing'
        ? [
            ['Invoice', 'Business group', 'Period start', 'Period end', 'Total', 'Currency', 'Status'],
            ...(invoices.data?.items ?? []).map((i) => [
              i.invoice_number,
              i.business_group_name ?? '',
              i.period_start,
              i.period_end,
              i.total,
              i.currency,
              i.status,
            ]),
          ]
        : tab === 'payouts'
          ? [
              ['Business group', 'Period', 'Collected', 'Share %', 'Payout', 'Currency', 'Status'],
              ...(payouts.data?.items ?? []).map((p) => [
                p.business_group_name,
                p.period_start,
                p.gross_collected,
                p.revenue_share_pct,
                p.payout_amount,
                p.currency,
                p.status,
              ]),
            ]
          : [
              ['Business group', 'TRN', 'Currency', 'Transactions', 'Gross', 'Tax collected'],
              ...(tax.data?.items ?? []).map((t) => [
                t.business_group ?? '',
                t.tax_registration_number ?? '',
                t.currency,
                String(t.transactions),
                t.gross_amount,
                t.tax_collected,
              ]),
            ];

    const csv = rows
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `netegate-${tab}-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Financials &amp; tax</h1>
          <p>Billing, payouts and tax collected, per business group.</p>
        </div>
        <button onClick={exportCsv}>Export report</button>
      </div>

      <div className="tabs">
        <button className={tab === 'billing' ? 'active' : ''} onClick={() => setTab('billing')}>
          Billing
        </button>
        <button className={tab === 'payouts' ? 'active' : ''} onClick={() => setTab('payouts')}>
          Payouts
        </button>
        <button className={tab === 'tax' ? 'active' : ''} onClick={() => setTab('tax')}>
          Tax reports
        </button>
      </div>

      <ErrorBanner error={invoices.error ?? payouts.error ?? tax.error} />

      {tab === 'tax' ? (
        <div className="filters">
          <div className="field">
            <label htmlFor="from">From</label>
            <input id="from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="to">To</label>
            <input id="to" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
        </div>
      ) : null}

      <section className="panel">
        {tab === 'billing' ? (
          invoices.loading ? (
            <Loading />
          ) : invoices.data?.items.length ? (
            <table>
              <thead>
                <tr>
                  <th>Invoice</th>
                  <th>Business group</th>
                  <th>Period</th>
                  <th className="num">Total</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {invoices.data.items.map((invoice) => (
                  <tr key={invoice.id}>
                    <td className="mono">{invoice.invoice_number}</td>
                    <td>{invoice.business_group_name ?? '—'}</td>
                    <td className="muted">
                      {shortDate(invoice.period_start)} – {shortDate(invoice.period_end)}
                    </td>
                    <td className="num">{money(invoice.total, invoice.currency)}</td>
                    <td>
                      <Status value={invoice.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <Empty>No invoices raised yet.</Empty>
          )
        ) : null}

        {tab === 'payouts' ? (
          payouts.loading ? (
            <Loading />
          ) : payouts.data?.items.length ? (
            <table>
              <thead>
                <tr>
                  <th>Business group</th>
                  <th>Period</th>
                  <th className="num">Collected</th>
                  <th className="num">Share</th>
                  <th className="num">Payout</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {payouts.data.items.map((payout) => (
                  <tr key={payout.id}>
                    <td>{payout.business_group_name}</td>
                    <td className="muted">{shortDate(payout.period_start)}</td>
                    <td className="num">{money(payout.gross_collected, payout.currency)}</td>
                    <td className="num muted">{Number(payout.revenue_share_pct)}%</td>
                    <td className="num">{money(payout.payout_amount, payout.currency)}</td>
                    <td>
                      <Status value={payout.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <Empty>No payouts calculated yet.</Empty>
          )
        ) : null}

        {tab === 'tax' ? (
          tax.loading ? (
            <Loading />
          ) : tax.data?.items.length ? (
            <table>
              <thead>
                <tr>
                  <th>Business group</th>
                  <th>TRN</th>
                  <th className="num">Transactions</th>
                  <th className="num">Gross</th>
                  <th className="num">Tax collected</th>
                </tr>
              </thead>
              <tbody>
                {tax.data.items.map((row, index) => (
                  <tr key={index}>
                    <td>{row.business_group ?? '—'}</td>
                    <td className="mono muted">{row.tax_registration_number ?? '—'}</td>
                    <td className="num">{row.transactions}</td>
                    <td className="num">{money(row.gross_amount, row.currency)}</td>
                    <td className="num">{money(row.tax_collected, row.currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <Empty>No taxable activity in that period.</Empty>
          )
        ) : null}
      </section>
    </>
  );
}
